import { motion, AnimatePresence } from 'framer-motion';
import { Form, Input, Button, Card } from 'antd';
import { Check, X, Send, PlayCircle } from 'lucide-react';
import { FaRobot } from 'react-icons/fa';
import { ReadOutlined } from '@ant-design/icons';
import type { FormInstance } from 'antd/es/form';
import type { UseMutationResult } from '@tanstack/react-query';
import {
	SuggestionConfiguration,
	ApiConfigurationSuggestionsResponse,
	ApplySuggestionResponse,
	AnimationComponents,
} from '@/types/suggestions';
import './CopilotSider.scss';

interface CopilotSiderProps {
	open: boolean;
	toggleDrawer: (open: boolean) => void;
	form: FormInstance;
	getSuggestions: UseMutationResult<
		ApiConfigurationSuggestionsResponse,
		any,
		{ query: string; agentId: string },
		unknown
	>;

	suggestions: SuggestionConfiguration[];
	displayMessage: string;
	handleSubmit: () => void;
	hasAppliedChanges: boolean;
	showTestBtn: boolean;
	onTest?: () => void;
	applySuggestion: UseMutationResult<
		ApplySuggestionResponse,
		any,
		SuggestionConfiguration,
		unknown
	>;
	handleRejectSuggestions: () => void;
	handleAcceptSuggestion: () => void;
	renderSuggestionItem: (suggestion: SuggestionConfiguration) => React.ReactNode;
	LoadingAnimation: AnimationComponents['LoadingAnimation'];
	TypingAnimation: AnimationComponents['TypingAnimation'];
}

const drawerVariants = {
	hidden: { x: '100%' },
	visible: {
		x: '0%',
		transition: {
			type: 'spring',
			stiffness: 200,
			damping: 30,
			when: 'beforeChildren',
		},
	},
	exit: {
		x: '100%',
		transition: {
			type: 'spring',
			stiffness: 200,
			damping: 30,
			when: 'beforeChildren',
			duration: 2,
		},
	},
};

const CopilotSider = ({
	open,
	toggleDrawer,
	form,
	getSuggestions,
	suggestions,
	displayMessage,
	handleSubmit,
	hasAppliedChanges,
	showTestBtn,
	onTest,
	applySuggestion,
	handleRejectSuggestions,
	handleAcceptSuggestion,
	renderSuggestionItem,
	LoadingAnimation,
	TypingAnimation,
}: CopilotSiderProps) => {
	const handleCloseDrawer = () => {
		toggleDrawer(false);
		handleRejectSuggestions();
	};

	return (
		<AnimatePresence mode="wait">
			{open && (
				<>
					<motion.div
						key="framer-drawer-overlay"
						className="fixed inset-0 bg-black bg-opacity-50 z-[1299]"
						onClick={handleCloseDrawer}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
					/>

					<motion.div
						key="framer-drawer"
						className="ai-copilot-content fixed top-0 right-0 h-screen bg-white shadow-lg overflow-y-auto p-5 max-w-fit w-full sm:w-1/2 md:w-1/3 lg:w-2/5 z-[1300] fixed"
						initial="hidden"
						animate="visible"
						exit="exit"
						variants={drawerVariants}
					>
						<motion.div
							key="copilot"
							initial={{ opacity: 0, x: -20 }}
							animate={{ opacity: 1, x: 0 }}
							exit={{ opacity: 0, x: 20 }}
							transition={{ duration: 0.3 }}
							className="tab-content"
						>
							<div className="input-container">
								<div className="content-header flex-col sm:flex-row">
									<button
										onClick={handleCloseDrawer}
										className="w-10 h-10 mr-auto flex items-center justify-center text-[#6366f1] hover:bg-gray-200 rounded-full transition-transform duration-300 hover:rotate-[180deg]"
									>
										✕
									</button>
									<div className="icon-wrapper">
										<FaRobot size={24} className="text-blue-600" />
									</div>
									<div className="header-text">
										<h2>AI Copilot</h2>
										<p>
											Just let me know what you would like created / changed in your AI
											Receptionist.
										</p>
									</div>
								</div>

								<div className="input-section">
									<Form form={form} onFinish={handleSubmit} className="prompt-form">
										<motion.div
											animate={{
												boxShadow: [
													'0 0 0 0 rgba(99, 102, 241, 0)',
													'0 0 0 4px rgba(99, 102, 241, 0.1)',
													'0 0 0 0 rgba(99, 102, 241, 0)',
												],
											}}
											transition={{
												duration: 2,
												repeat: Infinity,
												ease: 'easeInOut',
											}}
											className="relative prompt-input-container"
										>
											<motion.div
												className="absolute top-0 right-0 mt-2 mr-3 bg-blue-100 text-blue-600 text-xs font-semibold px-2 py-1 rounded-full"
												animate={{
													y: [0, -3, 0],
												}}
												transition={{
													duration: 1.5,
													repeat: Infinity,
													ease: 'easeInOut',
												}}
											>
												Type here
											</motion.div>
											<Form.Item
												name="prompt"
												rules={[{ required: true, message: 'Please enter a prompt' }]}
											>
												<Input.TextArea
													placeholder="Example: Make my AI ask for the caller's company name"
													autoSize={{ minRows: 3, maxRows: 6 }}
													className="prompt-input"
												/>
											</Form.Item>
										</motion.div>
									</Form>

									<div className="quick-suggestions">
										<p className="suggestion-label">Quick suggestions:</p>
										<div className="suggestion-buttons">
											<button
												className="suggestion-button"
												onClick={() =>
													form.setFieldsValue({
														prompt:
															'I want my AI to transfer calls to +1 (800) 123-4567 when the caller wants to speak with a human.',
													})
												}
											>
												I want my AI to transfer calls
											</button>
											<button
												className="suggestion-button"
												onClick={() =>
													form.setFieldsValue({
														prompt:
															'I want my AI to send a text message with my meeting scheduling link: https://example.com/my-link, when callers ask to book an appointment.',
													})
												}
											>
												I want my AI to send text messages
											</button>
											<button
												className="suggestion-button"
												onClick={() =>
													form.setFieldsValue({
														prompt:
															"I want my AI to collect callers' names and email addresses when they request a follow-up.",
													})
												}
											>
												I want my AI to collect caller's information
											</button>
										</div>
									</div>

									<Button
										type="primary"
										onClick={handleSubmit}
										loading={getSuggestions.isPending}
										icon={<Send size={18} />}
										className="generate-button"
									>
										Update now
									</Button>

									{showTestBtn && !suggestions.length && hasAppliedChanges && (
										<div className="test-button-container">
											<Button
												type="primary"
												onClick={onTest}
												icon={<PlayCircle size={20} />}
												className="test-button"
											>
												Test your changes →
											</Button>
										</div>
									)}
								</div>
							</div>

							{getSuggestions.isPending ? (
								<motion.div
									className="suggestions-section"
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
								>
									<Card className="suggestions-card">
										<div className="suggestions-header">
											<h2 className="font-bolf text-xl">Generating Suggestions...</h2>
										</div>
										<LoadingAnimation />
									</Card>
								</motion.div>
							) : displayMessage ? (
								<motion.div
									className="suggestions-section"
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
								>
									<div className="message-container">
										<TypingAnimation text={displayMessage.trim()} />
										{suggestions.length > 0 ? (
											<>
												<div className="suggestions-content">
													{renderSuggestionItem(suggestions[0])}
												</div>
												<div className="action-buttons">
													<Button
														type="default"
														icon={<X size={18} />}
														onClick={handleRejectSuggestions}
														className="reject-button"
														disabled={applySuggestion.isPending}
													>
														Discard
													</Button>
													<Button
														type="primary"
														icon={<Check size={18} />}
														onClick={handleAcceptSuggestion}
														loading={applySuggestion.isPending}
														className="accept-button"
													>
														Apply update
													</Button>
												</div>
											</>
										) : getSuggestions.data?.suggestions?.tutorial ? (
											<div className="tutorial-container p-4">
												<p className="text-gray-600 mb-3">
													Here's a helpful tutorial that might assist you:
												</p>
												<a
													href={getSuggestions.data.suggestions.tutorial.url}
													target="_blank"
													rel="noopener noreferrer"
													className="tutorial-link inline-flex items-center px-6 py-3 bg-white text-blue-600 rounded-lg hover:bg-blue-100 transition-colors duration-200 font-medium shadow-sm"
												>
													<span className="mr-2">
														<ReadOutlined />
													</span>
													{getSuggestions.data.suggestions.tutorial.title}
												</a>
											</div>
										) : null}
									</div>
								</motion.div>
							) : null}
						</motion.div>
					</motion.div>
				</>
			)}
		</AnimatePresence>
	);
};

export default CopilotSider;
